import { useState } from 'react'
import { Alert, Button, Group, List, Modal, Stack, Text } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'

import {
  homologarRelease, usePreviaHomologacao, type MotivoHomologacao, type MotivoPulado,
} from '@/api/robo'

/** Por que ainda não dá para homologar — cada frase diz o que fazer. */
export const MOTIVO: Record<MotivoHomologacao, string> = {
  sem_prova: 'Nenhum robô rodou esta versão pelo ar ainda. Atualize um robô de bancada primeiro.',
  ja_homologada: 'Esta versão já está homologada.',
  abaixo_da_homologada: 'Há uma versão mais nova homologada. Para voltar a frota, publique uma release nova.',
  versao_desconhecida: 'Esta versão não está no changelog do modelo.',
}

/** Por que um robô do modelo fica de fora da atualização. */
export const PULADO: Record<MotivoPulado, string> = {
  ja_na_versao: 'já está nesta versão',
  candidata: 'está numa versão mais nova',
  desenvolvimento: 'roda um build de desenvolvimento',
  modelo_divergente: 'informa ser de outro modelo',
  modelo_nao_cadastrado: 'não tem o modelo cadastrado',
  ordem_ativa: 'já tem uma atualização em andamento',
  nao_ativo: 'não está ativo',
  anterior: 'tem firmware anterior ao versionamento',
}

const RECUSA: Record<string, string> = {
  ...MOTIVO,
  firmware_nao_publicado: 'Esta versão ainda não foi publicada no catálogo de firmware.',
  catalogo_indisponivel: 'O catálogo de firmware não respondeu. Tente de novo.',
  ordem_ativa: 'Um robô ganhou uma atualização enquanto você confirmava. Nada foi homologado — tente de novo.',
}

function fraseDoErro(e: unknown): string {
  const detail = isAxiosError(e) ? (e.response?.data as { detail?: unknown })?.detail : undefined
  return (typeof detail === 'string' && RECUSA[detail]) || 'Não foi possível homologar. Tente de novo.'
}

/**
 * Homologar a candidata mais nova: aprova a release para a frota e cria a
 * ordem de atualização de cada robô do modelo que está atrás (spec
 * 2026-10-02, 4.6). O diálogo mostra antes quem recebe e quem fica de fora.
 */
export function HomologarRelease({ perfil, versao }: { perfil: string; versao: string }) {
  const qc = useQueryClient()
  const [aberto, ctl] = useDisclosure(false)
  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const previa = usePreviaHomologacao(perfil, versao)
  const p = previa.data
  if (!p) return null
  const n = p.alvos.length

  const confirmar = async () => {
    setEnviando(true)
    setErro(null)
    try {
      await homologarRelease(perfil, versao)
      ctl.close()
    } catch (e) {
      setErro(fraseDoErro(e))
    } finally {
      setEnviando(false)
      await Promise.all(['robo-releases', 'robo-frota', 'robo-detalhe', 'robo-homologacao', 'robo-ota']
        .map((k) => qc.invalidateQueries({ queryKey: [k] })))
    }
  }

  return (
    <Stack gap={4}>
      <Group>
        <Button size="xs" variant="light" disabled={!p.pode} onClick={() => { setErro(null); ctl.open() }}>
          Homologar
        </Button>
      </Group>
      {!p.pode && p.motivo && <Text size="xs" c="dimmed">{MOTIVO[p.motivo]}</Text>}

      <Modal opened={aberto} onClose={ctl.close} title={`Homologar a ${versao}`}>
        <Stack gap="sm">
          <Text size="sm">
            {n === 0 ? 'Nenhum robô vai receber' : n === 1 ? '1 robô vai receber' : `${n} robôs vão receber`} a {versao}.
            {n > 0 && ' Cada um baixa e reinicia sozinho, e o dono é avisado.'}
          </Text>
          {n > 0 && (
            <List size="sm">
              {p.alvos.map((a) => <List.Item key={a.public_id}>{a.nome || a.public_id}</List.Item>)}
            </List>
          )}
          {p.pulados.length > 0 && (
            <List size="sm" c="dimmed">
              {p.pulados.map((x) => (
                <List.Item key={x.public_id}>{`${x.nome || x.public_id} fica de fora: ${PULADO[x.motivo]}`}</List.Item>
              ))}
            </List>
          )}
          {p.prova && (
            <Text size="xs" c="dimmed">{`Prova de bancada: ordem ${p.prova.id}, aplicada pelo ar.`}</Text>
          )}
          {erro && <Alert color="red">{erro}</Alert>}
          <Group justify="flex-end">
            <Button variant="default" onClick={ctl.close}>Cancelar</Button>
            <Button loading={enviando} onClick={confirmar}>Homologar e atualizar a frota</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  )
}
